/**
 * API Error Handler - Centralized error handling with Hindi toast messages
 * Wraps fetch calls and shows user-friendly Hindi error messages via sonner toast
 */
import { toast } from 'sonner';

type ApiErrorType = 'network' | 'auth' | 'server' | 'validation' | 'notfound' | 'unknown';

const ERROR_MESSAGES: Record<ApiErrorType, string> = {
  network: 'इंटरनेट कनेक्शन में समस्या है। कृपया अपना नेटवर्क जाँचें।',
  auth: 'सत्र समाप्त हो गया है। कृपया दोबारा लॉगिन करें।',
  server: 'सर्वर में कुछ समस्या आई। कृपया थोड़ी देर बाद प्रयास करें।',
  validation: 'कृपया सही जानकारी भरें।',
  notfound: 'अनुरोधित डेटा नहीं मिला।',
  unknown: 'कुछ गड़बड़ हो गई। कृपया पुनः प्रयास करें।',
};

function classifyError(error: any, status?: number): ApiErrorType {
  if (!navigator.onLine || error?.message?.includes('fetch') || error?.message?.includes('network') || error?.name === 'TypeError') {
    return 'network';
  }
  if (status === 401) return 'auth';
  if (status === 403) return 'validation'; // 403 = no admin access, NOT session expired
  if (status === 404) return 'notfound';
  if (status === 422 || status === 400) return 'validation';
  if (status && status >= 500) return 'server';
  return 'unknown';
}

export function showApiError(error: any, status?: number, customMessage?: string) {
  const type = classifyError(error, status);
  const message = customMessage || ERROR_MESSAGES[type];

  toast.error(message, {
    duration: 4000,
    position: 'top-center',
  });

  // Auto-redirect to login on auth errors
  if (type === 'auth') {
    setTimeout(() => {
      localStorage.removeItem('session_token');
      localStorage.removeItem('ram_naam_user');
      window.location.reload();
    }, 2000);
  }
}

/**
 * Safe fetch wrapper - automatically handles errors and shows Hindi toasts
 * Returns null on error instead of throwing
 */
export async function safeFetch<T = any>(
  url: string,
  options?: RequestInit,
  opts?: { silent?: boolean; errorMessage?: string }
): Promise<{ data: T; ok: true } | { data: null; ok: false }> {
  try {
    const res = await fetch(url, options);

    if (!res.ok) {
      let detail = '';
      try {
        const json = await res.json();
        detail = json.detail || json.message || '';
      } catch {}

      if (!opts?.silent) {
        showApiError(null, res.status, detail || opts?.errorMessage);
      }
      return { data: null, ok: false };
    }

    const data = await res.json();
    return { data, ok: true };
  } catch (error) {
    if (!opts?.silent) {
      showApiError(error, undefined, opts?.errorMessage);
    }
    return { data: null, ok: false };
  }
}
